import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ResearchId, ResearchRequirement } from '../constants';

import { ProgressBar } from './ProgressBar';

type ResearchTreeNode = {
  id: ResearchId;
  name: string;
  status: 'unlocked' | 'available' | 'locked';
  prerequisites: readonly ResearchId[];
  isRepeatable: boolean;
  repeatableLevel: number;
  atMaxLevel: boolean;
};

type SelectedResearchProgress = {
  progressValue: number;
  progressComplete: boolean;
  showProgressBar: boolean;
  completedText: string;
  label: string;
  requirements: { requirement: ResearchRequirement; current: number }[];
  totalCollected: number;
  totalRequired: number;
  hasRequirements: boolean;
  isRepeatable: boolean;
  repeatableLevel: number;
  atMaxLevel: boolean;
  status: 'unlocked' | 'available' | 'locked';
};

type NodePosition = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type PositionedNode = ResearchTreeNode & { level: number };

type ResearchTreeProps = {
  nodes: readonly ResearchTreeNode[];
  selectedResearchId: ResearchId | null;
  onSelect: (researchId: ResearchId) => void;
  onClearSelection: () => void;
  selectedProgress: SelectedResearchProgress | null;
  debugModeEnabled?: boolean;
  onForceUnlock?: (researchId: ResearchId) => void;
};

const getStatusLabel = (
  node: ResearchTreeNode,
  isUnlocked: boolean,
  isSelected: boolean,
  isAvailable: boolean
) => {
  if (node.isRepeatable) {
    if (node.atMaxLevel) {
      return `Level ${node.repeatableLevel} (Max)`;
    }
    if (isSelected) {
      return `Level ${node.repeatableLevel} • Active`;
    }
    if (node.repeatableLevel > 0) {
      return `Level ${node.repeatableLevel}`;
    }
    return isAvailable ? 'Available' : 'Locked';
  }

  if (isUnlocked) {
    return 'Unlocked';
  }
  if (isSelected) {
    return 'Active';
  }
  if (isAvailable) {
    return 'Available';
  }
  return 'Locked';
};

const summarizeRequirement = (
  requirement: ResearchRequirement,
  current: number
) => {
  return `${requirement.amount}x ${requirement.item} (${Math.min(
    current,
    requirement.amount
  )}/${requirement.amount})`;
};

export const ResearchTree: React.FC<ResearchTreeProps> = ({
  nodes,
  selectedResearchId,
  onSelect,
  onClearSelection,
  selectedProgress,
  debugModeEnabled = false,
  onForceUnlock,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const nodeRefs = useRef<Record<ResearchId, HTMLDivElement | null>>(
    {} as Record<ResearchId, HTMLDivElement | null>
  );
  const [nodePositions, setNodePositions] = useState<
    Partial<Record<ResearchId, NodePosition>>
  >({});
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  const nodesById = useMemo(() => {
    const map = new Map<ResearchId, ResearchTreeNode>();
    nodes.forEach(node => {
      map.set(node.id, node);
    });
    return map;
  }, [nodes]);

  const selectedNode = useMemo(
    () =>
      selectedResearchId ? (nodesById.get(selectedResearchId) ?? null) : null,
    [nodesById, selectedResearchId]
  );

  const positionedNodes = useMemo(() => {
    const memo = new Map<ResearchId, number>();

    const resolveLevel = (id: ResearchId): number => {
      const cached = memo.get(id);
      if (cached !== undefined) {
        return cached;
      }

      const node = nodesById.get(id);
      if (!node || node.prerequisites.length === 0) {
        memo.set(id, 0);
        return 0;
      }

      const prerequisiteLevels = node.prerequisites.map(prerequisiteId =>
        resolveLevel(prerequisiteId)
      );
      const level = Math.max(...prerequisiteLevels) + 1;
      memo.set(id, level);
      return level;
    };

    const buckets = new Map<number, PositionedNode[]>();
    nodes.forEach(node => {
      const level = resolveLevel(node.id);
      const bucket = buckets.get(level);
      const positionedNode: PositionedNode = {
        ...node,
        level,
      };

      if (bucket) {
        bucket.push(positionedNode);
      } else {
        buckets.set(level, [positionedNode]);
      }
    });

    return Array.from(buckets.keys())
      .sort((a, b) => a - b)
      .map(level => buckets.get(level) ?? [])
      .map(levelNodes =>
        levelNodes.sort((a, b) => a.name.localeCompare(b.name))
      );
  }, [nodes, nodesById]);

  const updatePositions = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const scrollLeft = container.scrollLeft;
    const scrollTop = container.scrollTop;
    const nextPositions: Record<ResearchId, NodePosition> = {} as Record<
      ResearchId,
      NodePosition
    >;

    nodes.forEach(node => {
      const element = nodeRefs.current[node.id];
      if (!element) {
        return;
      }

      const rect = element.getBoundingClientRect();
      nextPositions[node.id] = {
        x: rect.left - containerRect.left + scrollLeft + rect.width / 2,
        y: rect.top - containerRect.top + scrollTop + rect.height / 2,
        width: rect.width,
        height: rect.height,
      };
    });

    setNodePositions(nextPositions);
    setContainerSize({
      width: Math.max(container.scrollWidth, containerRect.width),
      height: Math.max(container.scrollHeight, containerRect.height),
    });
  }, [nodes]);

  useLayoutEffect(() => {
    const frame = requestAnimationFrame(() => {
      updatePositions();
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [nodes, selectedProgress, selectedResearchId, updatePositions]);

  useEffect(() => {
    window.addEventListener('resize', updatePositions);
    return () => {
      window.removeEventListener('resize', updatePositions);
    };
  }, [updatePositions]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    const handleScroll = () => {
      updatePositions();
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [updatePositions]);

  const connections = useMemo(() => {
    const edges: {
      id: string;
      from: NodePosition;
      to: NodePosition;
      isSatisfied: boolean;
      isActive: boolean;
    }[] = [];

    nodes.forEach(node => {
      const targetPosition = nodePositions[node.id];
      if (!targetPosition) {
        return;
      }

      node.prerequisites.forEach(prerequisiteId => {
        const prerequisitePosition = nodePositions[prerequisiteId];
        if (!prerequisitePosition) {
          return;
        }

        const prerequisiteNode = nodesById.get(prerequisiteId);
        const targetNode = nodesById.get(node.id);

        const prerequisiteUnlocked = prerequisiteNode
          ? prerequisiteNode.isRepeatable
            ? prerequisiteNode.repeatableLevel > 0 ||
              prerequisiteNode.atMaxLevel
            : prerequisiteNode.status === 'unlocked'
          : false;

        const targetAvailable = targetNode
          ? targetNode.status !== 'locked'
          : false;

        edges.push({
          id: `${prerequisiteId}->${node.id}`,
          from: prerequisitePosition,
          to: targetPosition,
          isSatisfied: prerequisiteUnlocked,
          isActive: targetAvailable,
        });
      });
    });

    return edges;
  }, [nodePositions, nodes, nodesById]);

  const renderSelectedSummary = () => {
    if (!selectedNode || !selectedProgress) {
      return (
        <div className='research-tree__selected-card research-tree__selected-card--empty'>
          <div className='research-tree__selected-card-header'>
            <div className='research-tree__selected-card-title-group'>
              <span className='research-tree__selected-card-title'>
                Selected Research
              </span>
              <span className='research-tree__selected-card-name'>None</span>
            </div>
          </div>
          <div className='research-tree__selected-progress-placeholder'>
            Select a research node to view its requirements.
          </div>
        </div>
      );
    }

    const statusLabel = getStatusLabel(
      selectedNode,
      selectedProgress.status === 'unlocked',
      true,
      selectedProgress.status === 'available'
    );

    const hasDetails =
      selectedProgress.showProgressBar || selectedProgress.hasRequirements;

    const requirementSummary = selectedProgress.hasRequirements
      ? `${Math.min(
          selectedProgress.totalCollected,
          selectedProgress.totalRequired
        )}/${selectedProgress.totalRequired} items gathered`
      : 'No resources required';

    const placeholderMessage = (() => {
      if (selectedProgress.status === 'unlocked') {
        return 'This research has already been completed.';
      }
      if (selectedProgress.status === 'locked') {
        return 'Meet the prerequisites to make this research available.';
      }
      return 'This research has no resource requirements.';
    })();

    return (
      <div className='research-tree__selected-card'>
        <div className='research-tree__selected-card-header'>
          <div className='research-tree__selected-card-title-group'>
            <span className='research-tree__selected-card-title'>
              Selected Research
            </span>
            <span className='research-tree__selected-card-name'>
              {selectedNode.name}
            </span>
          </div>
          <span
            className={`research-tree__selected-card-status research-tree__selected-card-status--${selectedProgress.status}`}
          >
            {statusLabel}
          </span>
        </div>
        {hasDetails ? (
          <div className='research-tree__selected-progress'>
            {selectedProgress.showProgressBar ? (
              <ProgressBar
                progress={selectedProgress.progressValue}
                label={selectedProgress.label}
                isComplete={selectedProgress.progressComplete}
                completedText={selectedProgress.completedText}
              />
            ) : null}
            <div className='research-tree__selected-progress-summary'>
              {requirementSummary}
            </div>
            {selectedProgress.requirements.length > 0 ? (
              <ul className='research-tree__selected-requirements'>
                {selectedProgress.requirements.map(
                  ({ requirement, current }) => (
                    <li key={`${requirement.item}-${requirement.amount}`}>
                      {summarizeRequirement(requirement, current)}
                    </li>
                  )
                )}
              </ul>
            ) : null}
          </div>
        ) : (
          <div className='research-tree__selected-progress-placeholder'>
            {placeholderMessage}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className='research-tree'>
      <div className='research-tree__header'>
        <div>
          <h4>Tech Tree Overview</h4>
          <p>Review research dependencies and status at a glance.</p>
        </div>
        {renderSelectedSummary()}
      </div>
      <div className='research-tree__graph' ref={containerRef}>
        <svg
          className='research-tree__connections'
          width={Math.max(containerSize.width, 1)}
          height={Math.max(containerSize.height, 1)}
          viewBox={`0 0 ${Math.max(containerSize.width, 1)} ${Math.max(
            containerSize.height,
            1
          )}`}
        >
          {connections.map(connection => {
            const startX = connection.from.x + connection.from.width / 2;
            const startY = connection.from.y;
            const endX = connection.to.x - connection.to.width / 2;
            const endY = connection.to.y;
            const midX = (startX + endX) / 2;

            const path = `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`;

            const connectionClassNames = [
              'research-tree__connection',
              connection.isSatisfied
                ? 'research-tree__connection--complete'
                : connection.isActive
                  ? 'research-tree__connection--active'
                  : 'research-tree__connection--locked',
            ].join(' ');

            return (
              <path
                key={connection.id}
                d={path}
                className={connectionClassNames}
              />
            );
          })}
        </svg>
        <div
          className='research-tree__columns'
          style={{
            gridTemplateColumns: `repeat(${positionedNodes.length || 1}, minmax(220px, 1fr))`,
          }}
        >
          {positionedNodes.map((columnNodes, columnIndex) => (
            <div
              key={`column-${columnIndex}`}
              className='research-tree__column'
            >
              {columnNodes.map(node => {
                const refSetter = (element: HTMLDivElement | null) => {
                  nodeRefs.current[node.id] = element;
                };

                const isSelected = selectedResearchId === node.id;
                const isUnlocked = node.isRepeatable
                  ? node.repeatableLevel > 0 || node.atMaxLevel
                  : node.status === 'unlocked';
                const isAvailable = node.status === 'available';
                const showSetActiveButton = node.isRepeatable
                  ? !node.atMaxLevel
                  : !isUnlocked;
                const canSelect =
                  showSetActiveButton && (isSelected || !node.atMaxLevel);
                const isInteractive = canSelect || isSelected;

                const showForceUnlockButton =
                  debugModeEnabled && typeof onForceUnlock === 'function';
                const canForceUnlock = node.isRepeatable
                  ? !node.atMaxLevel
                  : !isUnlocked;

                const statusLabel = getStatusLabel(
                  node,
                  isUnlocked,
                  isSelected,
                  isAvailable
                );

                const handleActivate = () => {
                  if (isSelected) {
                    onClearSelection();
                    return;
                  }

                  if (!canSelect) {
                    return;
                  }

                  onSelect(node.id);
                };

                const handleKeyDown: React.KeyboardEventHandler<
                  HTMLDivElement
                > = event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleActivate();
                  }
                };

                const handleForceUnlock: React.MouseEventHandler<
                  HTMLButtonElement
                > = event => {
                  event.stopPropagation();
                  if (!showForceUnlockButton || !canForceUnlock) {
                    return;
                  }

                  onForceUnlock?.(node.id);
                };

                return (
                  <div
                    key={node.id}
                    ref={refSetter}
                    className={[
                      'research-tree__node',
                      `research-tree__node--${node.status}`,
                      isUnlocked ? 'research-tree__node--unlocked' : '',
                      isSelected ? 'research-tree__node--selected' : '',
                      isInteractive ? 'research-tree__node--interactive' : '',
                      node.isRepeatable
                        ? 'research-tree__node--repeatable'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={handleActivate}
                    onKeyDown={isInteractive ? handleKeyDown : undefined}
                    role={isInteractive ? 'button' : undefined}
                    tabIndex={isInteractive ? 0 : -1}
                    aria-pressed={isSelected || undefined}
                    aria-label={`${node.name} – ${statusLabel}`}
                  >
                    <div className='research-tree__node-title'>{node.name}</div>
                    <div className='research-tree__node-status'>
                      {statusLabel}
                    </div>
                    {node.prerequisites.length > 0 ? (
                      <div className='research-tree__node-prerequisites'>
                        <span>Requires:</span>
                        <ul>
                          {node.prerequisites.map(prerequisiteId => {
                            const prerequisite = nodesById.get(prerequisiteId);
                            const prerequisiteUnlocked = prerequisite
                              ? prerequisite.isRepeatable
                                ? prerequisite.repeatableLevel > 0 ||
                                  prerequisite.atMaxLevel
                                : prerequisite.status === 'unlocked'
                              : false;
                            return (
                              <li
                                key={`${node.id}-${prerequisiteId}`}
                                className={
                                  prerequisiteUnlocked
                                    ? 'research-tree__node-prereq--met'
                                    : 'research-tree__node-prereq--unmet'
                                }
                              >
                                {prerequisite?.name ?? prerequisiteId}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ) : (
                      <div className='research-tree__node-prerequisites research-tree__node-prerequisites--none'>
                        <span>Base Tech</span>
                      </div>
                    )}
                    {isSelected && selectedProgress ? (
                      <div className='research-tree__node-progress'>
                        {selectedProgress.showProgressBar ? (
                          <ProgressBar
                            progress={selectedProgress.progressValue}
                            label={selectedProgress.label}
                            isComplete={selectedProgress.progressComplete}
                            completedText={selectedProgress.completedText}
                          />
                        ) : null}
                        {selectedProgress.requirements.length > 0 ? (
                          <ul className='research-tree__node-progress-list'>
                            {selectedProgress.requirements.map(
                              ({ requirement, current }) => (
                                <li
                                  key={`${requirement.item}-${requirement.amount}`}
                                >
                                  {summarizeRequirement(requirement, current)}
                                </li>
                              )
                            )}
                          </ul>
                        ) : null}
                      </div>
                    ) : null}
                    {showForceUnlockButton ? (
                      <div className='research-tree__node-debug'>
                        <button
                          type='button'
                          className='research-tree__force-unlock'
                          onClick={handleForceUnlock}
                          disabled={!canForceUnlock}
                        >
                          Force Unlock
                        </button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
